/**
 * Resolve which local Cipher is bound to a Space channel or category.
 *
 * A public Space has no Space-wide Cipher. Encrypting one channel must not
 * make that Cipher the fallback for every other channel — otherwise a
 * category encrypted with Cipher B can silently send traffic under Cipher A.
 *
 * @module services/resolveLinkedCipherId
 */

import type { CipherCheck } from '@adieuu/shared';
import {
  getCategoryCipherLink,
  getChannelCipherLink,
  getSpaceCipherLink,
} from './spaceCipherService';

export function cipherChecksMatch(
  a: CipherCheck | null | undefined,
  b: CipherCheck | null | undefined,
): boolean {
  if (!a || !b) return false;
  return a.nonce === b.nonce && a.encryptedKnownValue === b.encryptedKnownValue;
}

export interface ResolveLinkedCipherIdArgs {
  spaceId: string;
  spaceE2ee?: boolean;
  spaceCipherCheck?: CipherCheck | null;
  channelId?: string | null;
  categoryId?: string | null;
  entityCipherCheck?: CipherCheck | null;
}

/**
 * Local cipher id for this entity, or null when none is safely bound.
 *
 * Preference: channel link → category link → Space link only when this
 * entity actually shares the Space challenge (or the Space is e2ee and
 * the entity has no override challenge).
 */
export function resolveLinkedCipherId(args: ResolveLinkedCipherIdArgs): string | null {
  if (args.channelId) {
    const channelLink = getChannelCipherLink(args.channelId);
    if (channelLink) return channelLink;
  }
  if (args.categoryId) {
    const categoryLink = getCategoryCipherLink(args.categoryId);
    if (categoryLink) return categoryLink;
  }

  const spaceLink = getSpaceCipherLink(args.spaceId);
  if (!spaceLink) return null;

  if (args.entityCipherCheck) {
    return cipherChecksMatch(args.entityCipherCheck, args.spaceCipherCheck)
      ? spaceLink
      : null;
  }

  return args.spaceE2ee ? spaceLink : null;
}
